import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { FREE_TIER_MESSAGE_LIMIT, MIN_PASSWORD_LENGTH } from '@/lib/constants'
import { runAsync } from '@/lib/runAsync'
import FormField from '@/components/shared/FormField'
import AuthLoadingShell from './AuthLoadingShell'
import PasswordField from './PasswordField'
import { getSafeRedirect } from './authRouting'

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l3.56-2.77.01-.54z"
        fill="#FBBC05"
      />
      <path
        d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z"
        fill="#EA4335"
      />
    </svg>
  )
}

export default function Login() {
  const searchParams = new URLSearchParams(window.location.search)
  const redirectParam = searchParams.get('redirect')
  const redirectTo = getSafeRedirect(redirectParam)
  const urlMode = searchParams.get('mode')
  const initialMode = urlMode === 'signup' || redirectTo.startsWith('/redeem') ? 'signup' : 'login'
  const [mode, setMode] = useState(initialMode)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // A11Y-009: per-field errors so screen readers announce the right
  // failing field on submit (linked via aria-describedby in FormField).
  // formError stays as a separate alert region for server-side errors
  // ("incorrect password", network failure, etc.) that don't tie to a
  // single field.
  const [nameError, setNameError] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState(null)
  const [formError, setFormError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [signupSent, setSignupSent] = useState(false)

  function clearAllErrors() {
    setNameError(null)
    setEmailError(null)
    setPasswordError(null)
    setConfirmPasswordError(null)
    setFormError(null)
  }
  const navigate = useNavigate()
  const session = useSession()
  const { profile } = useProfile()
  const auth = {
    user: session.user,
    loading: session.loading,
    profile,
    signUpWithEmail: session.signUpWithEmail,
    signInWithEmail: session.signInWithEmail,
    signInWithGoogle: session.signInWithGoogle,
    resetPasswordForEmail: session.resetPasswordForEmail,
  }

  // After signup or sign-in, route the user based on their state:
  // - Fresh users with onboarding NOT completed → /onboarding (the activation
  //   path). Without this, fresh signups land on `/` and bounce — the dominant
  //   cause of the post-soft-launch 0% activation rate (see
  //   briefs/onboarding-research-2026-05-03.md).
  // - Returning users with onboarding completed → honor the safe-validated
  //   ?redirect= param (or `/` if none).
  useEffect(() => {
    if (auth.loading) return
    if (auth.user && auth.profile) {
      const target = auth.profile.onboardingCompleted ? redirectTo : '/onboarding'
      navigate(target, { replace: true })
    }
  }, [auth.user, auth.profile, auth.loading, navigate, redirectTo])

  async function handleSubmit(e) {
    e.preventDefault()
    clearAllErrors()

    // Per-field validation — set errors per input so SR announces the
    // failing field via aria-describedby. Collect all client-side
    // failures before short-circuiting so the user sees every problem
    // at once rather than fixing one + resubmitting to find the next.
    let hasError = false
    if (mode === 'signup' && !fullName.trim()) {
      setNameError('Please enter your name.')
      hasError = true
    }
    if (!email) {
      setEmailError('Email is required.')
      hasError = true
    }
    if (!password) {
      setPasswordError('Password is required.')
      hasError = true
    } else if (mode === 'signup' && password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      hasError = true
    }
    if (mode === 'signup' && password && password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.')
      hasError = true
    }
    if (hasError) return

    // Navigation handled by the useEffect watching auth state. Server
    // errors (incorrect password, email taken, network) land in
    // formError below the form — they're not tied to one specific input.
    // Signup with email confirmation pending returns needsConfirmation;
    // we render a success card instead of waiting for an auth-state change
    // that won't come until the confirmation link is clicked.
    const result = await runAsync(
      () =>
        mode === 'signup'
          ? auth.signUpWithEmail(email, password, fullName.trim())
          : auth.signInWithEmail(email, password),
      { setError: setFormError, setLoading: setSubmitting }
    )
    if (mode === 'signup' && result?.needsConfirmation) {
      setSignupSent(true)
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault()
    clearAllErrors()
    if (!email) {
      setEmailError('Please enter your email address.')
      return
    }
    const result = await runAsync(() => auth.resetPasswordForEmail(email), {
      setError: setFormError,
      setLoading: setSubmitting,
    })
    if (!result?.error) setForgotSent(true)
  }

  async function handleGoogle() {
    // Google OAuth redirects away on success; loading stays true until the
    // browser navigates. runAsync still handles error + loading if the
    // redirect fails to start.
    await runAsync(() => auth.signInWithGoogle(), {
      setError: setFormError,
      setLoading: setSubmitting,
    })
    // Google OAuth redirects to Supabase, then back — no navigate needed
  }

  // Auth is loading, or user exists but profile is still being created (OAuth callback)
  if (auth.loading || (auth.user && !auth.profile)) {
    return <AuthLoadingShell variant="inline" message="Signing you in..." />
  }

  // Signup — email confirmation pending success state
  if (mode === 'signup' && signupSent) {
    return (
      <div className="max-w-md w-full">
        <img
          src="/illustrations/correct-answer.svg"
          alt=""
          className="w-28 mx-auto mb-6 opacity-30"
        />
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-4">
            Confirm Your Email
          </h2>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            We sent a confirmation link to <span className="text-gold">{email}</span>. Click the
            link to activate your account, then come back and log in.
          </p>
          <button
            onClick={() => {
              setMode('login')
              setSignupSent(false)
              clearAllErrors()
            }}
            className="text-gold text-sm hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  // Forgot password — success state
  if (mode === 'forgot' && forgotSent) {
    return (
      <div className="max-w-md w-full">
        <img
          src="/illustrations/correct-answer.svg"
          alt=""
          className="w-28 mx-auto mb-6 opacity-30"
        />
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-4">
            Check Your Email
          </h2>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            We sent a password reset link to <span className="text-gold">{email}</span>. Check your
            inbox and click the link to set a new password.
          </p>
          <button
            onClick={() => {
              setMode('login')
              setForgotSent(false)
              clearAllErrors()
            }}
            className="text-gold text-sm hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  // Forgot password — form
  if (mode === 'forgot') {
    return (
      <div className="max-w-md w-full">
        <img
          src="/illustrations/mindfulness.svg"
          alt=""
          loading="lazy"
          className="w-24 mx-auto mb-6 opacity-30"
        />
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10">
          <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-2 text-center">
            Reset Password
          </h2>
          <p className="text-[var(--text-muted)] text-sm text-center mb-8">
            Enter your email and we'll send you a reset link.
          </p>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <FormField label="Email address" srLabel error={emailError} required>
              {props => (
                <input
                  {...props}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all"
                />
              )}
            </FormField>
            {formError && (
              <p role="alert" className="text-[var(--feedback-negative)] text-sm text-center">
                {formError}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--gold)] hover:brightness-110 text-[var(--text-on-gold)] font-bold py-3.5 rounded-lg text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
            >
              {submitting ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <button
            onClick={() => {
              setMode('login')
              clearAllErrors()
            }}
            className="w-full text-center text-[var(--text-muted)] text-sm mt-4 hover:text-gold transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md w-full">
      {/* Decorative illustration */}
      <img
        src="/illustrations/step-to-the-sun.svg"
        alt=""
        className="w-28 mx-auto mb-6 opacity-30"
      />
      <div className="glass-panel p-8 md:p-10 relative overflow-hidden">
        {/* Warm glow behind card */}
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-40 bg-[var(--gold)]/8 blur-3xl rounded-full pointer-events-none" />

        {/* Tab toggle */}
        <div className="relative flex border-b border-[var(--glass-border)] mb-8">
          <button
            onClick={() => {
              setMode('login')
              clearAllErrors()
            }}
            className={`flex-1 pb-3 text-sm font-semibold tracking-wide transition-colors ${mode === 'login' ? 'text-[var(--gold)] border-b-2 border-[var(--gold)]' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            Log In
          </button>
          <button
            onClick={() => {
              setMode('signup')
              clearAllErrors()
            }}
            className={`flex-1 pb-3 text-sm font-semibold tracking-wide transition-colors ${mode === 'signup' ? 'text-[var(--gold)] border-b-2 border-[var(--gold)]' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            Sign Up
          </button>
        </div>

        <h2 className="relative font-heading text-[var(--text-primary)] text-2xl font-bold mb-2 text-center">
          {mode === 'login' ? 'Welcome Back' : 'Begin Your Journey'}
        </h2>
        <p className="relative text-[var(--text-muted)] text-sm text-center mb-2">
          {mode === 'login'
            ? 'Pick up right where you left off.'
            : `Create your free account — ${FREE_TIER_MESSAGE_LIMIT} messages/month, no credit card required.`}
        </p>
        {mode === 'signup' && (
          <div className="relative flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[var(--text-muted-soft)] text-xs mb-6">
            <span>Scripture study</span>
            <span className="text-[var(--gold)]">+</span>
            <span>Apologetics</span>
            <span className="text-[var(--gold)]">+</span>
            <span>Sermon prep</span>
            <span className="text-[var(--gold)]">+</span>
            <span>Bible study</span>
          </div>
        )}
        {mode === 'login' && <div className="mb-6" />}

        {/* Google button */}
        <button
          onClick={handleGoogle}
          disabled={submitting}
          className="relative w-full bg-white text-earth-brown font-semibold py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-warm-cream transition-all disabled:opacity-50 shadow-sm"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        {/* Divider */}
        <div className="relative flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-[var(--glass-border)]" />
          <span className="text-[var(--text-muted)] text-xs uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-[var(--glass-border)]" />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <FormField label="Full name" srLabel error={nameError} required>
              {props => (
                <input
                  {...props}
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Full name"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all"
                />
              )}
            </FormField>
          )}

          <FormField label="Email address" srLabel error={emailError} required>
            {props => (
              <input
                {...props}
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all"
              />
            )}
          </FormField>

          <PasswordField
            label="Password"
            placeholder="Password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={passwordError}
            className="w-full px-4 py-3 pr-12 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all"
          />

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setMode('forgot')
                  clearAllErrors()
                }}
                className="text-[var(--text-muted)] text-sm hover:text-[var(--gold)] transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          {mode === 'signup' && (
            <PasswordField
              label="Confirm password"
              placeholder="Confirm password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              error={confirmPasswordError}
              className="w-full px-4 py-3 pr-12 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all"
            />
          )}

          {formError && (
            <p role="alert" className="text-[var(--feedback-negative)] text-sm text-center">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="relative w-full bg-[var(--gold)] hover:brightness-110 text-[var(--text-on-gold)] font-bold py-3.5 rounded-xl text-lg transition-all duration-300 hover:shadow-[0_4px_20px_var(--glow-color)] disabled:opacity-50 disabled:hover:shadow-none"
          >
            {submitting ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}
          </button>
        </form>

        {/* Trust signals */}
        {mode === 'signup' && (
          <div className="relative mt-6 pt-5 border-t border-[var(--glass-border)] flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[var(--text-muted-soft)] text-xs">
            <span>No credit card required</span>
            <span>Cancel anytime</span>
            <span>Your data stays private</span>
          </div>
        )}
      </div>
    </div>
  )
}
